<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Card, Table, TableHead, TableHeadCell, TableBody, TableBodyRow, TableBodyCell,
		Button, Modal, Label, Input, Select, Textarea, Toggle, Checkbox, Spinner, Badge
	} from 'flowbite-svelte';
	import PageHeader from '../../components/PageHeader.svelte';
	import RowAction from '../../components/admin/RowAction.svelte';
	import {
		listIncidents, createIncident, addIncidentUpdate, deleteIncident, listServices
	} from '../../AppNetwork';
	import type { Incident, AdminService } from '../../AppTypes';

	let incidents: Incident[] = [];
	let services: AdminService[] = [];
	let loading = true;
	let error = '';

	let showModal = false;
	let form = blankForm();
	let selectedServiceIds: number[] = [];
	let isSaving = false;

	let showUpdateModal = false;
	let updateTarget: Incident | null = null;
	let updateForm = { status: 'identified', body: '' };

	const incidentStatuses = ['investigating', 'identified', 'monitoring', 'resolved'];
	const maintenanceStatuses = ['scheduled', 'in_progress', 'completed'];

	function blankForm() {
		return { type: 'incident' as 'incident' | 'maintenance', title: '', impact: 'minor', status: 'investigating', body: '', isPublished: true, scheduledStart: '', scheduledEnd: '' };
	}

	function formatDate(dateString: string | null): string {
		if (!dateString) return '—';
		return new Date(dateString).toLocaleDateString(undefined, {
			month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
		});
	}

	function statusBadge(status: string): 'green' | 'blue' | 'yellow' | 'dark' {
		if (status === 'resolved' || status === 'completed') return 'green';
		if (status === 'monitoring' || status === 'in_progress') return 'blue';
		if (status === 'scheduled') return 'dark';
		return 'yellow';
	}

	async function loadAll() {
		loading = true;
		error = '';
		try {
			[incidents, services] = await Promise.all([
				listIncidents({ limit: 100 }).then((r) => r.items),
				listServices()
			]);
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load incidents';
		} finally {
			loading = false;
		}
	}
	onMount(loadAll);

	function openCreate() {
		form = blankForm(); selectedServiceIds = []; error = ''; showModal = true;
	}

	async function save() {
		isSaving = true;
		error = '';
		try {
			const payload: Record<string, unknown> = {
				type: form.type, title: form.title, impact: form.impact, status: form.status,
				isPublished: form.isPublished, body: form.body || undefined, serviceIds: selectedServiceIds.map(Number)
			};
			if (form.type === 'maintenance') {
				payload.scheduledStart = form.scheduledStart || null;
				payload.scheduledEnd = form.scheduledEnd || null;
			}
			await createIncident(payload);
			showModal = false;
			await loadAll();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to create incident';
		}
		isSaving = false;
	}

	function openUpdate(incident: Incident) {
		updateTarget = incident;
		updateForm = { status: incident.type === 'maintenance' ? 'in_progress' : 'identified', body: '' };
		showUpdateModal = true;
	}
	async function saveUpdate() {
		if (!updateTarget) return;
		isSaving = true;
		try {
			await addIncidentUpdate(updateTarget.id, updateForm);
			showUpdateModal = false;
			await loadAll();
		} finally {
			isSaving = false;
		}
	}

	async function remove(incident: Incident) {
		if (!confirm(`Delete incident "${incident.title}"? This cannot be undone.`)) return;
		await deleteIncident(incident.id);
		await loadAll();
	}

	function toggleService(id: number, checked: boolean) {
		selectedServiceIds = checked ? [...selectedServiceIds, id] : selectedServiceIds.filter((s) => s !== id);
	}

	$: statusOptions = form.type === 'maintenance' ? maintenanceStatuses : incidentStatuses;
</script>

<PageHeader title="Incidents" description="Post incidents and schedule maintenance windows shown on the public page." />

<div class="space-y-6">
	<Card class="max-w-none">
		<div class="flex justify-end">
			<Button on:click={openCreate}>
				<i class="fa-solid fa-plus me-2"></i>New Incident / Maintenance
			</Button>
		</div>
	</Card>

	<Card padding="none" class="max-w-none">
		{#if loading}
			<div class="p-8 text-center">
				<i class="fa-solid fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
				<p class="text-gray-500 dark:text-gray-400">Loading incidents...</p>
			</div>
		{:else if error}
			<div class="p-8 text-center">
				<i class="fa-solid fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">{error}</p>
				<Button class="mt-4" on:click={loadAll}>Retry</Button>
			</div>
		{:else if incidents.length === 0}
			<div class="p-8 text-center">
				<i class="fa-solid fa-clipboard-list text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">No incidents</p>
			</div>
		{:else}
			<Table striped divClass="relative overflow-x-auto rounded-lg">
				<TableHead class="bg-gray-50 dark:bg-gray-700">
					<TableHeadCell>Title</TableHeadCell>
					<TableHeadCell>Type</TableHeadCell>
					<TableHeadCell>Status</TableHeadCell>
					<TableHeadCell>Started</TableHeadCell>
					<TableHeadCell>Actions</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each incidents as incident}
						<TableBodyRow>
							<TableBodyCell class="font-medium text-gray-900 dark:text-white">
								{incident.title}
								{#if incident.autoCreated}<Badge class="ml-2" color="yellow">auto</Badge>{/if}
							</TableBodyCell>
							<TableBodyCell class="text-gray-500 dark:text-gray-400">{incident.type}</TableBodyCell>
							<TableBodyCell><Badge color={statusBadge(incident.status)}>{incident.status.replace(/_/g, ' ')}</Badge></TableBodyCell>
							<TableBodyCell class="text-gray-500 dark:text-gray-400">{formatDate(incident.startedAt)}</TableBodyCell>
							<TableBodyCell>
								<div class="flex gap-1">
									<RowAction icon="fa-comment-medical" label="Add update" on:click={() => openUpdate(incident)} />
									<RowAction icon="fa-trash" label="Delete" danger on:click={() => remove(incident)} />
								</div>
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		{/if}
	</Card>
</div>

<Modal bind:open={showModal} title="New Incident / Maintenance" size="lg">
	<div class="space-y-4">
		<div class="grid grid-cols-2 gap-4">
			<div><Label class="mb-1">Type</Label>
				<Select bind:value={form.type}>
					<option value="incident">Incident</option>
					<option value="maintenance">Maintenance</option>
				</Select>
			</div>
			<div><Label class="mb-1">Impact</Label>
				<Select bind:value={form.impact}>
					<option value="none">None</option>
					<option value="minor">Minor</option>
					<option value="major">Major</option>
					<option value="critical">Critical</option>
				</Select>
			</div>
		</div>
		<div><Label class="mb-1">Title</Label><Input bind:value={form.title} /></div>
		<div><Label class="mb-1">Initial status</Label>
			<Select bind:value={form.status}>
				{#each statusOptions as s}<option value={s}>{s}</option>{/each}
			</Select>
		</div>
		{#if form.type === 'maintenance'}
			<div class="grid grid-cols-2 gap-4">
				<div><Label class="mb-1">Scheduled start</Label><Input type="datetime-local" bind:value={form.scheduledStart} /></div>
				<div><Label class="mb-1">Scheduled end</Label><Input type="datetime-local" bind:value={form.scheduledEnd} /></div>
			</div>
		{/if}
		<div><Label class="mb-1">Initial update message</Label><Textarea bind:value={form.body} rows={3} /></div>
		<div>
			<Label class="mb-1">Affected services</Label>
			<div class="mt-2 space-y-1 max-h-40 overflow-auto">
				{#each services as service}
					<Checkbox checked={selectedServiceIds.includes(service.id)} on:change={(e) => toggleService(service.id, e.currentTarget.checked)}>{service.name}</Checkbox>
				{/each}
			</div>
		</div>
		<Toggle bind:checked={form.isPublished}>Published</Toggle>
		{#if error}<p class="text-red-500 text-sm">{error}</p>{/if}
	</div>
	<svelte:fragment slot="footer">
		<div class="flex gap-2">
			<Button color="primary" on:click={save} disabled={isSaving || !form.title}>
				{#if isSaving}<Spinner size="sm" class="mr-2" />{/if}Create
			</Button>
			<Button color="alternative" on:click={() => (showModal = false)}>Cancel</Button>
		</div>
	</svelte:fragment>
</Modal>

<Modal bind:open={showUpdateModal} title="Add update" size="md">
	<div class="space-y-4">
		<div><Label class="mb-1">Status</Label>
			<Select bind:value={updateForm.status}>
				{#each [...incidentStatuses, ...maintenanceStatuses] as s}<option value={s}>{s}</option>{/each}
			</Select>
		</div>
		<div><Label class="mb-1">Message</Label><Textarea bind:value={updateForm.body} rows={3} /></div>
	</div>
	<svelte:fragment slot="footer">
		<div class="flex gap-2">
			<Button color="primary" on:click={saveUpdate} disabled={isSaving || !updateForm.body}>
				{#if isSaving}<Spinner size="sm" class="mr-2" />{/if}Post update
			</Button>
			<Button color="alternative" on:click={() => (showUpdateModal = false)}>Cancel</Button>
		</div>
	</svelte:fragment>
</Modal>
